import type { Project } from '../types';
import { parseJSON } from '../components/ProjectForm';

// Raw shape of a milestone as stored in `project.planning.milestones[]`
// (snake_case, mirrors ProjectForm's formToPayload — not the camelCase
// `Milestone` client-form type, which only exists inside the editor).
export interface RawMilestone {
  type?: 'standard' | 'process';
  id: string;
  name: string;
  contents?: string;
  status: string; // 'pending' | 'active' | 'completed' | 'cancelled'
  estimated_start: string; // 'YYYY-MM-DD'
  estimated_end: string;   // 'YYYY-MM-DD'
  assignments?: { name: string; note: string }[];
  acceptance?: string;
  acceptance_deliverables?: string[];
  due_date_history?: { old_date: string | null; new_date: string | null; changed_at: string; changed_by: string }[];
  start_date_history?: { old_date: string | null; new_date: string | null; changed_at: string; changed_by: string }[];
}

// Cancelled milestones are left out: they stay in the planning (greyed, in
// their place) but count nowhere — progress, Gérance, deadlines.
export function getProjectMilestones(project: Pick<Project, 'planning'>): RawMilestone[] {
  const ms = parseJSON<any>(project.planning, {}).milestones;
  return Array.isArray(ms) ? ms.filter((m: RawMilestone) => m?.status !== 'cancelled') : [];
}

export interface MilestoneProgress {
  total: number;
  completed: number;
  currentIndex: number; // 0-based index into the milestones array
  current: RawMilestone;
}

// "Current" milestone = first non-completed one (in order), or the last one
// if every milestone is already completed — so a fully-closed plan still
// shows something instead of nothing.
export function getMilestoneProgress(milestones: RawMilestone[]): MilestoneProgress | null {
  if (milestones.length === 0) return null;
  const completed = milestones.filter(m => m.status === 'completed').length;
  const idx = milestones.findIndex(m => m.status !== 'completed');
  const currentIndex = idx === -1 ? milestones.length - 1 : idx;
  return { total: milestones.length, completed, currentIndex, current: milestones[currentIndex] };
}

// Timeline state of the milestone at `index`: completed → 'done', the current
// one (see getMilestoneProgress) → 'active', anything else → 'pending'.
export function getMilestoneState(m: RawMilestone, index: number, progress: MilestoneProgress | null): 'done' | 'active' | 'pending' {
  if (m.status === 'completed') return 'done';
  return progress && index === progress.currentIndex ? 'active' : 'pending';
}
